import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { compressImage } from '../utils/image'

/** 도면은 치수가 보여야 하므로 약하게 압축 (긴 변 2400px, JPEG 85%) */
const DRAW_MAXDIM = 2400
const DRAW_QUALITY = 0.85

export default function Drawings({ profile }) {
  const [folders, setFolders] = useState([])
  const [drawings, setDrawings] = useState([])
  const [activeFolder, setActiveFolder] = useState(null) // 선택된 폴더 (null = 폴더 목록)
  const [showFolderModal, setShowFolderModal] = useState(false)
  const [folderName, setFolderName] = useState('')
  const [showUpload, setShowUpload] = useState(false)
  const [upForm, setUpForm] = useState({ title:'', memo:'' })
  const [upFile, setUpFile] = useState(null)
  const [upPreview, setUpPreview] = useState('')
  const [viewer, setViewer] = useState(null) // 전체화면으로 볼 도면
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => { fetchFolders() }, [])
  useEffect(() => { if (activeFolder) fetchDrawings(activeFolder.id) }, [activeFolder])

  // 폴더 진입·뷰어 열기를 뒤로가기 단계로 만들기 (hash는 그대로 둬 페이지 유지)
  function openFolder(f) { window.history.pushState({ d:'folder' }, ''); setActiveFolder(f) }
  function openViewer(d) { window.history.pushState({ d:'viewer' }, ''); setViewer(d) }
  useEffect(() => {
    function onPop() {
      if (viewer) { setViewer(null); return }
      if (activeFolder) setActiveFolder(null)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [viewer, activeFolder])

  async function fetchFolders() {
    const { data, error } = await supabase.from('drawing_folders').select('*').order('sort').order('created_at')
    if (error) console.error('drawing_folders 조회:', error.message)
    if (data) setFolders(data)
  }

  async function fetchDrawings(folderId) {
    const { data, error } = await supabase.from('production_drawings').select('*').eq('folder_id', folderId).order('created_at', { ascending: false })
    if (error) console.error('production_drawings 조회:', error.message)
    if (data) setDrawings(data)
  }

  // 폴더별 도면 수 (배지용)
  const [counts, setCounts] = useState({})
  useEffect(() => {
    if (!folders.length) return
    let active = true
    ;(async () => {
      const { data } = await supabase.from('production_drawings').select('folder_id')
      if (!active || !data) return
      const c = {}
      data.forEach(d => { c[d.folder_id] = (c[d.folder_id] || 0) + 1 })
      setCounts(c)
    })()
    return () => { active = false }
  }, [folders, drawings])

  async function addFolder() {
    if (!folderName.trim()) { alert('폴더명을 입력하세요'); return }
    const { error } = await supabase.from('drawing_folders').insert({ name: folderName.trim(), sort: folders.length })
    if (error) {
      alert('생성 실패: ' + error.message + '\n\nSupabase에 drawing_folders 테이블이 있는지 확인하세요 (supabase/sql/drawings.sql 실행)')
      return
    }
    setFolderName('')
    setShowFolderModal(false)
    fetchFolders()
  }

  async function deleteFolder(f) {
    if (!window.confirm(`'${f.name}' 폴더를 삭제할까요?\n안에 있는 도면도 함께 삭제됩니다.`)) return
    const { error } = await supabase.from('drawing_folders').delete().eq('id', f.id)
    if (error) { alert('삭제 실패: ' + error.message); return }
    if (activeFolder?.id === f.id) setActiveFolder(null)
    fetchFolders()
  }

  async function handleFile(e) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    const r = new FileReader()
    r.onload = ev => setUpPreview(ev.target.result)
    r.readAsDataURL(file)
    setUpFile(file)
    if (!upForm.title) setUpForm(f => ({ ...f, title: file.name.replace(/\.[^.]+$/, '') }))
  }

  async function submitDrawing() {
    if (!upFile) { alert('도면 이미지를 선택하세요'); return }
    if (!upForm.title.trim()) { alert('도면 이름을 입력하세요'); return }
    setLoading(true)
    let blob
    try {
      blob = await compressImage(upFile, DRAW_MAXDIM, DRAW_QUALITY)
    } catch (err) {
      alert('이미지 처리 실패: ' + err.message); setLoading(false); return
    }
    const fileName = `drawing_${Date.now()}.jpg`
    const { data, error } = await supabase.storage.from('drawings').upload(fileName, blob, { contentType: 'image/jpeg' })
    if (error) { alert('업로드 실패: ' + error.message); setLoading(false); return }
    const { data: urlData } = supabase.storage.from('drawings').getPublicUrl(data.path)
    const { error: insErr } = await supabase.from('production_drawings').insert({
      folder_id: activeFolder.id,
      title: upForm.title.trim(),
      image_url: urlData.publicUrl,
      memo: upForm.memo,
      created_by_name: profile.name,
    })
    if (insErr) { alert('저장 실패: ' + insErr.message); setLoading(false); return }
    setShowUpload(false)
    setUpForm({ title:'', memo:'' })
    setUpFile(null)
    setUpPreview('')
    fetchDrawings(activeFolder.id)
    setLoading(false)
  }

  async function deleteDrawing(d) {
    if (!window.confirm(`'${d.title}' 도면을 삭제할까요?`)) return
    const { error } = await supabase.from('production_drawings').delete().eq('id', d.id)
    if (error) { alert('삭제 실패: ' + error.message); return }
    window.history.back() // 뷰어 닫기 (pushState 단계 소비)
    fetchDrawings(activeFolder.id)
  }

  const inputStyle = { width:'100%', padding:'9px 12px', border:'1px solid #e5e3de', borderRadius:8, fontSize:13, fontFamily:'inherit', boxSizing:'border-box' }
  const labelStyle = { display:'block', fontSize:12, fontWeight:600, color:'#6b6860', marginBottom:6 }

  const filteredDrawings = drawings.filter(d => {
    const q = search.toLowerCase()
    if (!q) return true
    return d.title?.toLowerCase().includes(q) || d.memo?.toLowerCase().includes(q)
  })

  /* ——— 폴더 목록 화면 ——— */
  if (!activeFolder) {
    return (
      <div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:10 }}>
          <div>
            <div style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>양산도면</div>
            <div style={{ fontSize:13, color:'#a09e99' }}>폴더를 선택해 도면을 확인하세요 · 현장에서 바로 보고 절곡</div>
          </div>
          <button onClick={() => setShowFolderModal(true)}
            style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
            + 폴더 추가
          </button>
        </div>

        {folders.length === 0 ? (
          <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, padding:60, textAlign:'center', color:'#a09e99' }}>
            <div style={{ fontSize:36, marginBottom:10 }}>📁</div>
            <div style={{ fontSize:13 }}>+ 폴더 추가로 도면 분류를 만드세요 (예: 냉동탑, 윙바디)</div>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(150px,1fr))', gap:14 }}>
            {folders.map(f => (
              <div key={f.id} onClick={() => openFolder(f)}
                style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:12, padding:'20px 16px', cursor:'pointer', boxShadow:'0 1px 3px rgba(0,0,0,0.06)', position:'relative' }}>
                <div style={{ fontSize:40, marginBottom:8 }}>📁</div>
                <div style={{ fontSize:15, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{f.name}</div>
                <div style={{ fontSize:12, color:'#a09e99', marginTop:2 }}>도면 {counts[f.id] || 0}장</div>
                <button onClick={e => { e.stopPropagation(); deleteFolder(f) }}
                  style={{ position:'absolute', top:8, right:8, border:'none', background:'transparent', color:'#cfcdc8', cursor:'pointer', fontSize:14 }}
                  title="폴더 삭제">🗑️</button>
              </div>
            ))}
          </div>
        )}

        {showFolderModal && (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
            onClick={e => e.target === e.currentTarget && setShowFolderModal(false)}>
            <div style={{ background:'#fff', borderRadius:14, width:360, maxWidth:'95vw', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
              <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:15, fontWeight:700 }}>폴더 추가</span>
                <button onClick={() => setShowFolderModal(false)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
              </div>
              <div style={{ padding:'20px 24px' }}>
                <label style={labelStyle}>폴더명</label>
                <input value={folderName} onChange={e => setFolderName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addFolder()}
                  placeholder="예) 냉동탑, 윙바디, 측면판" autoFocus style={inputStyle} />
              </div>
              <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', gap:10, justifyContent:'flex-end' }}>
                <button onClick={() => setShowFolderModal(false)}
                  style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>취소</button>
                <button onClick={addFolder}
                  style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>만들기</button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  /* ——— 폴더 안 도면 그리드 ——— */
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
          <button onClick={() => window.history.back()}
            style={{ border:'1px solid #e5e3de', background:'#fff', borderRadius:8, padding:'7px 12px', cursor:'pointer', fontSize:13, flexShrink:0 }}>
            ← 폴더
          </button>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:18, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>📁 {activeFolder.name}</div>
            <div style={{ fontSize:12, color:'#a09e99' }}>도면 {drawings.length}장</div>
          </div>
        </div>
        <button onClick={() => setShowUpload(true)}
          style={{ padding:'8px 16px', borderRadius:8, border:'none', background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500, flexShrink:0 }}>
          + 도면 추가
        </button>
      </div>

      <div style={{ marginBottom:16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="도면 이름, 메모 검색..." style={inputStyle} />
      </div>

      {filteredDrawings.length === 0 ? (
        <div style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, padding:50, textAlign:'center', color:'#a09e99' }}>
          <div style={{ fontSize:32, marginBottom:8 }}>📐</div>
          <div style={{ fontSize:13 }}>+ 도면 추가로 이 폴더에 도면을 올리세요</div>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(160px,1fr))', gap:14 }}>
          {filteredDrawings.map(d => (
            <div key={d.id} onClick={() => openViewer(d)}
              style={{ background:'#fff', border:'1px solid #e5e3de', borderRadius:10, overflow:'hidden', cursor:'pointer', boxShadow:'0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ width:'100%', height:140, background:'#f4f3f0', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden' }}>
                <img src={d.image_url} alt={d.title} style={{ width:'100%', height:'100%', objectFit:'contain' }} />
              </div>
              <div style={{ padding:'10px 12px' }}>
                <div style={{ fontSize:13, fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d.title}</div>
                {d.memo && <div style={{ fontSize:11, color:'#a09e99', marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d.memo}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 업로드 모달 */}
      {showUpload && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={e => e.target === e.currentTarget && setShowUpload(false)}>
          <div style={{ background:'#fff', borderRadius:14, width:440, maxWidth:'95vw', maxHeight:'92vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid #e5e3de', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:15, fontWeight:700 }}>도면 추가 · {activeFolder.name}</span>
              <button onClick={() => setShowUpload(false)} style={{ border:'none', background:'#f9f8f6', borderRadius:6, width:28, height:28, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
            <div style={{ padding:'20px 24px' }}>
              <div style={{ marginBottom:14 }}>
                <label style={labelStyle}>도면 이미지 *</label>
                <input type="file" accept="image/*" onChange={handleFile} style={{ fontSize:12 }} />
                {upPreview && (
                  <img src={upPreview} alt="미리보기" style={{ width:'100%', maxHeight:200, objectFit:'contain', borderRadius:8, border:'1px solid #e5e3de', marginTop:10, background:'#f4f3f0' }} />
                )}
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={labelStyle}>도면 이름 *</label>
                <input value={upForm.title} onChange={e => setUpForm({...upForm, title:e.target.value})} placeholder="예) 측면판 절곡도" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>메모 (절곡 치수 등)</label>
                <textarea value={upForm.memo} onChange={e => setUpForm({...upForm, memo:e.target.value})}
                  placeholder="절곡 각도, 치수, 주의사항 등" rows={3}
                  style={{ ...inputStyle, resize:'vertical' }} />
              </div>
            </div>
            <div style={{ padding:'16px 24px', borderTop:'1px solid #e5e3de', display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setShowUpload(false)}
                style={{ padding:'8px 16px', border:'1px solid #e5e3de', borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13 }}>취소</button>
              <button onClick={submitDrawing} disabled={loading}
                style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#2d5a3d', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:500 }}>
                {loading ? '저장 중...' : '저장하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 전체화면 도면 뷰어 */}
      {viewer && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.92)', zIndex:250, display:'flex', flexDirection:'column' }}
          onClick={() => window.history.back()}>
          <div style={{ padding:'14px 16px', display:'flex', justifyContent:'space-between', alignItems:'center', color:'#fff', flexShrink:0 }}
            onClick={e => e.stopPropagation()}>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:15, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{viewer.title}</div>
              {viewer.memo && <div style={{ fontSize:12, color:'rgba(255,255,255,0.7)', marginTop:2 }}>{viewer.memo}</div>}
            </div>
            <div style={{ display:'flex', gap:8, flexShrink:0 }}>
              <a href={viewer.image_url} target="_blank" rel="noreferrer"
                style={{ padding:'6px 12px', borderRadius:8, background:'rgba(255,255,255,0.15)', color:'#fff', fontSize:12, textDecoration:'none' }}>원본</a>
              <button onClick={() => deleteDrawing(viewer)}
                style={{ padding:'6px 12px', border:'none', borderRadius:8, background:'rgba(184,48,48,0.85)', color:'#fff', cursor:'pointer', fontSize:12 }}>삭제</button>
              <button onClick={() => window.history.back()}
                style={{ border:'none', background:'rgba(255,255,255,0.15)', color:'#fff', borderRadius:8, width:32, height:32, cursor:'pointer', fontSize:16 }}>✕</button>
            </div>
          </div>
          <div style={{ flex:1, overflow:'auto', display:'flex', alignItems:'center', justifyContent:'center', padding:'0 8px 16px' }}
            onClick={e => e.stopPropagation()}>
            <img src={viewer.image_url} alt={viewer.title}
              style={{ maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} />
          </div>
          <div style={{ textAlign:'center', color:'rgba(255,255,255,0.4)', fontSize:11, paddingBottom:12 }}>
            바깥을 누르면 닫힙니다 · 손가락으로 확대해서 치수를 확인하세요
          </div>
        </div>
      )}
    </div>
  )
}
